import { useTranslation } from 'react-i18next';
import { Input } from '~/components/ui';
import type { OrderInfo } from 'src/pages/order-builder/OrderBuider';
import { CartCard } from './CartCard';
import type { Cart } from '~/graphql/graphql';
import { CartSummary } from './CartSummary';

type OrderCalculatorProps = {
  onOrderInfoChange: (name: string, value: string) => void;
  onTargetOrderSizeBlur?: () => void;
  orderInfo: OrderInfo;
  averageNPrice: number;
  nCount: number;
  formattedMarketMidPriceWithUnit?: string | null;
  carts: Cart[];
};
export function OrderCalculator({
  onOrderInfoChange,
  onTargetOrderSizeBlur,
  orderInfo,
  averageNPrice,
  nCount,
  formattedMarketMidPriceWithUnit,
  carts = [],
}: Readonly<OrderCalculatorProps>) {
  const { t } = useTranslation('order-builder');
  const totalCartItems = carts.reduce(
    (total, cart) => total + (cart.items?.length || 0),
    0
  );
  return (
    <div className="bg-neut-light rounded-lg border border-neut-border shadow-neut-100 sticky top-0">
      <div className="px-6 py-4 border-b border-neut-border bg-neut-50">
        <h2 className="text-lg font-semibold text-neut-prim">
          {t('order_builder.order_calculator.title')}
        </h2>
        <p className="text-sm text-neut-sec mt-1">
          {t('order_builder.order_calculator.description')}
        </p>
      </div>
      <div className="px-6 py-4 border-b border-neut-border space-y-4">
        <div
          className="flex items-end gap-2"
          onBlur={(e) => {
            if (
              (e.relatedTarget as HTMLInputElement | null)?.name !==
              'targetWeeksToCover'
            ) {
              onTargetOrderSizeBlur?.();
            }
          }}
        >
          <Input
            label={t('order_builder.order_calculator.target_order_size')}
            type="number"
            name={'targetOrderSize'}
            value={orderInfo.targetOrderSize?.toString()}
            onChange={onOrderInfoChange}
          />
          <span className="text-sm text-neut-sec pb-3">
            {t('order_builder.order_calculator.units.mbf')}
          </span>
        </div>
        <div className="flex items-end gap-2">
          <Input
            label={t('order_builder.order_calculator.target_weeks_to_cover')}
            type="number"
            name={'targetWeeksToCover'}
            value={orderInfo.targetWeeksToCover?.toString()}
            onChange={onOrderInfoChange}
          />
          <span className="text-sm text-neut-sec pb-3">
            {t('order_builder.order_calculator.units.weeks')}
          </span>
        </div>
        <div>
          <div className="flex items-end gap-2">
            <div className="flex flex-col w-full gap-2">
              <Input
                leftIcon={<span className="text-neut-prim">$</span>}
                type="number"
                name="targetPrice"
                value={orderInfo.targetPrice?.toString()}
                onChange={onOrderInfoChange}
                label={
                  <span className="ml-md mb-xxs text-sm font-medium text-neut-prim">
                    {t('order_builder.order_calculator.target_price')}{' '}
                    <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded font-medium whitespace-nowrap">
                      {t('order_builder.order_calculator.market_price', {
                        formattedPriceWithUnit: formattedMarketMidPriceWithUnit,
                      })}
                    </span>
                  </span>
                }
              />
            </div>
            <span className="text-sm text-neut-sec pb-3">
              {t('order_builder.order_calculator.units.per_mbf')}
            </span>
          </div>
          <p className="text-xs text-neut-500 mt-1">
            {t('order_builder.order_calculator.recommended_price', {
              averagePrice: averageNPrice.toFixed(2),
              count: nCount,
            })}
          </p>
        </div>
      </div>
      <div className="px-6 py-4 border-b border-neut-border">
        <h3
          data-testid="order-calculator-cart-count"
          className="text-sm font-semibold text-neut-900 mb-3"
        >
          {t('order_builder.order_calculator.cart_title', {
            count: totalCartItems,
          })}
        </h3>
        <div className="space-y-4" data-testid="order-calculator-carts">
          {carts?.map((cart) => (
            <CartCard
              key={cart.id}
              items={cart.items!}
              organization={cart.sellingOrganization}
            />
          ))}
        </div>
      </div>
      <CartSummary targetSize={orderInfo.targetOrderSize ?? 0} />
    </div>
  );
}
